/**
 * DD-3697 Price requests & price history. Mirrors the backend's price-request
 * types.graphql; `allowedActions` decides which buttons the UI shows.
 */
export enum PriceRequestStatus {
    DRAFT = 'DRAFT',
    SENT = 'SENT',
    RESPONDED = 'RESPONDED',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    CANCELLED = 'CANCELLED',
}

export enum PriceRequestSide {
    CHAIN = 'CHAIN',
    LAB = 'LAB',
}

export enum PriceRequestLineOutcome {
    UNCHANGED = 'UNCHANGED',
    CHANGED = 'CHANGED',
    WITHDRAWN = 'WITHDRAWN',
}

export enum PriceRequestAction {
    EDIT_DRAFT = 'EDIT_DRAFT',
    EDIT_PRICES = 'EDIT_PRICES',
    SEND = 'SEND',
    RESPOND = 'RESPOND',
    APPROVE = 'APPROVE',
    REJECT = 'REJECT',
    CANCEL = 'CANCEL',
    POST_MESSAGE = 'POST_MESSAGE',
}

export enum PriceHistoryEventType {
    PRICE_SET = 'PRICE_SET',
    WITHDRAWN = 'WITHDRAWN',
}

export enum PriceHistorySource {
    INITIAL = 'INITIAL',
    REQUEST = 'REQUEST',
    MANUAL = 'MANUAL',
    LAB_DIRECT = 'LAB_DIRECT',
}

export type PriceValues = {
    price?: number | null;
    tiotandvardPrice?: number | null;
    deliveryTime?: number | null;
};

export type PriceRequestParty = {
    _id: string;
    name?: string | null;
};

export type PriceRequestLine = {
    _id: string;
    action: string;
    category: string;
    actionNumber?: string | null;
    actionName?: string | null;
    current: PriceValues;
    currentEnabled: boolean;
    outcome: PriceRequestLineOutcome;
    proposed?: PriceValues | null;
    priceDelta?: number | null;
    pricePercent?: number | null;
};

/** DD-3822: the lab ranking the chain gives a category the lab has no price list for yet. */
export type PriceRequestCategoryRank = {
    category: string;
    rank: number;
};

/** DD-3822: a product the creator of a draft can include in it. */
export type PriceRequestProduct = {
    action: string;
    actionNumber?: string | null;
    actionName?: string | null;
    category: string;
    categoryName?: string | null;
    current: PriceValues;
    currentEnabled: boolean;
    /** False when the lab has no price list for the category: the chain then gives it a rank. */
    hasCategoryPriceList: boolean;
};

export type PriceRequestLineInput = {
    action: string;
    outcome: PriceRequestLineOutcome;
    proposed?: PriceValues | null;
};

export type PriceRequestStatusChange = {
    status: PriceRequestStatus;
    changedAt: string;
    changedByName?: string | null;
    changedBySide: PriceRequestSide;
};

export type PriceRequestSummary = {
    changed: number;
    unchanged: number;
    withdrawn: number;
};

export type PriceRequest = {
    _id: string;
    number: string;
    chain: PriceRequestParty;
    lab: PriceRequestParty;
    actionSet: string;
    actionSetTitle?: string | null;
    currency: string;
    initiatorSide: PriceRequestSide;
    status: PriceRequestStatus;
    note?: string | null;
    lines: PriceRequestLine[];
    categoryRanks: PriceRequestCategoryRank[];
    /** Empty unless the caller may edit the draft. */
    availableProducts: PriceRequestProduct[];
    /** Pass back to approvePriceRequest: approval fails if the lab changed the lines since. */
    revision: number;
    summary: PriceRequestSummary;
    statusLog: PriceRequestStatusChange[];
    createdByName?: string | null;
    createdAt: string;
    updatedAt: string;
    viewerSide: PriceRequestSide;
    allowedActions: PriceRequestAction[];
};

export type PriceRequestPage = {
    items: PriceRequest[];
    total: number;
};

export type PriceRequestMessage = {
    _id: string;
    text: string;
    senderName: string;
    senderSide: PriceRequestSide;
    createdAt: string;
    isOwn: boolean;
};

export type PriceHistoryEntry = {
    _id: string;
    type: PriceHistoryEventType;
    source: PriceHistorySource;
    values: PriceValues;
    previous?: PriceValues | null;
    previousEnabled?: boolean | null;
    priceDelta?: number | null;
    pricePercent?: number | null;
    currency: string;
    priceRequest?: string | null;
    priceRequestNumber?: string | null;
    changedByName?: string | null;
    effectiveAt: string;
};

export type ProductPriceHistory = {
    action: string;
    actionNumber?: string | null;
    actionName?: string | null;
    chain: PriceRequestParty;
    lab: PriceRequestParty;
    actionSet: string;
    currency: string;
    current?: PriceValues | null;
    isWithdrawn: boolean;
    entries: PriceHistoryEntry[];
};

export type PriceRequestCounterpart = {
    _id: string;
    name?: string | null;
    currency?: string | null;
    activeActionSet?: string | null;
};

export type CreatePriceRequestInput = {
    counterpartId: string;
    actionSetId?: string | null;
    actionIds?: string[] | null;
    note?: string | null;
};
